import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useApiClient } from '@/composables/use-api-client.ts';
import type { VotingScale } from '@pokerly/contracts';

export interface VotingScaleGroup {
    key: string;
    name: string;
    description: string | null;
    variants: VotingScale[];
}

export const useVotingScalesStore = defineStore('voting-scales', () => {
    const { execute, data, isLoading } = useApiClient<VotingScale[]>();
    const scales = ref<VotingScale[]>([]);
    const hasLoaded = ref(false);

    async function fetch() {
        if (isLoading.value) {
            return scales.value;
        }
        await execute('/voting-scales');
        scales.value = data.value ?? [];
        hasLoaded.value = true;
        return scales.value;
    }

    async function ensureLoaded() {
        if (hasLoaded.value && scales.value.length > 0) {
            return scales.value;
        }
        return await fetch();
    }

    const groups = computed<VotingScaleGroup[]>(() => {
        const grouped = new Map<string, VotingScaleGroup>();
        for (const scale of scales.value) {
            const key = scale.groupKey ?? scale.id;
            const current = grouped.get(key);
            if (!current) {
                grouped.set(key, {
                    key,
                    name: scale.name,
                    description: scale.description,
                    variants: [scale],
                });
                continue;
            }
            current.variants.push(scale);
        }
        return [...grouped.values()].map((group) => ({
            ...group,
            variants: [...group.variants].sort((left, right) => {
                if (left.variantKey === right.variantKey) {
                    return left.name.localeCompare(right.name);
                }
                return (left.variantKey ?? left.name).localeCompare(right.variantKey ?? right.name);
            }),
        }));
    });

    function $reset() {
        scales.value = [];
        hasLoaded.value = false;
    }

    return {
        scales,
        groups,
        isLoading,
        fetch,
        ensureLoaded,
        $reset,
    };
});
